import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Video, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VideoConferencingSettingsProps {
  meetingPlatform: string;
  meetingUrl: string;
  meetingId: string;
  meetingPasscode: string;
  meetingInstructions: string;
  onChange: (field: string, value: string) => void;
}

export function VideoConferencingSettings({
  meetingPlatform,
  meetingUrl,
  meetingId,
  meetingPasscode,
  meetingInstructions,
  onChange
}: VideoConferencingSettingsProps) {
  const getPlatformInstructions = () => {
    switch (meetingPlatform) {
      case 'zoom':
        return 'Enter your Zoom meeting URL (e.g., https://zoom.us/j/123456789) and optional meeting ID and passcode.';
      case 'teams':
        return 'Enter your Microsoft Teams meeting URL (e.g., https://teams.microsoft.com/l/meetup-join/...).';
      case 'google_meet':
        return 'Enter your Google Meet URL (e.g., https://meet.google.com/abc-defg-hij).';
      default:
        return 'Select a platform to enable live webinar functionality for this course.';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="h-5 w-5" />
          Live Webinar Integration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            {getPlatformInstructions()}
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="meeting-platform">Video Conferencing Platform</Label>
          <Select 
            value={meetingPlatform || 'none'} 
            onValueChange={(value) => onChange('meeting_platform', value)}
          >
            <SelectTrigger id="meeting-platform">
              <SelectValue placeholder="Select platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None (No Live Webinar)</SelectItem>
              <SelectItem value="zoom">Zoom</SelectItem>
              <SelectItem value="teams">Microsoft Teams</SelectItem>
              <SelectItem value="google_meet">Google Meet</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {meetingPlatform && meetingPlatform !== 'none' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="meeting-url">Meeting URL *</Label>
              <div className="flex gap-2">
                <Input
                  id="meeting-url"
                  placeholder="https://..."
                  value={meetingUrl}
                  onChange={(e) => onChange('meeting_url', e.target.value)}
                />
                {meetingUrl && (
                  <a 
                    href={meetingUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-primary hover:underline whitespace-nowrap"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Test
                  </a>
                )}
              </div>
            </div>

            {meetingPlatform === 'zoom' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="meeting-id">Meeting ID (Optional)</Label>
                  <Input
                    id="meeting-id"
                    placeholder="123 456 789"
                    value={meetingId}
                    onChange={(e) => onChange('meeting_id', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="meeting-passcode">Passcode (Optional)</Label>
                  <Input
                    id="meeting-passcode"
                    placeholder="Enter meeting passcode"
                    value={meetingPasscode}
                    onChange={(e) => onChange('meeting_passcode', e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="meeting-instructions">Additional Instructions (Optional)</Label>
              <Textarea
                id="meeting-instructions"
                placeholder="Add any special instructions for participants joining the webinar..."
                value={meetingInstructions}
                onChange={(e) => onChange('meeting_instructions', e.target.value)}
                rows={4}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
